import React, { useMemo, useState } from 'react'

import { NotificationContext } from '../../../lib/hooks/useNotification'

import { NotificationContextValue, NotificationPayload, NotificationType } from '../../../types'

type NotificationProviderProps = {
  /** Children React Components, most likely the App component */
  children: React.ReactNode
}

const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [notifPayload, setNotificationPayload] = useState<NotificationPayload | null>(null)
  // memo the callback events separately, otherwise we'll instantiate new callbacks
  // whenever the payload changes and cause issues with useEffect
  const events = useMemo<NotificationContextValue>(() => {
    const showSuccessNotif = (
      message: string,
      viewUrl?: string,
      viewAria?: string,
      onClose?: () => void
    ) =>
      setNotificationPayload({
        type: NotificationType.Success,
        message,
        onClose,
        viewUrl,
        viewAria
      })
    const showErrorNotif = (
      message: string,
      onRetry?: () => void,
      retryAria?: string,
      onClose?: () => void
    ) =>
      setNotificationPayload({ type: NotificationType.Error, message, onClose, onRetry, retryAria })
    const showInfoNotif = (
      message: string,
      viewUrl?: string,
      viewAria?: string,
      refresh?: boolean,
      onClose?: () => void
    ) =>
      setNotificationPayload({
        type: NotificationType.Info,
        message,
        onClose,
        viewUrl,
        viewAria,
        refresh
      })
    const showNotif = (payload: NotificationPayload) => setNotificationPayload(payload)
    const hideNotif = () => setNotificationPayload(null)
    return {
      notifPayload: null,
      showSuccessNotif,
      showErrorNotif,
      showInfoNotif,
      showNotif,
      hideNotif
    }
  }, [setNotificationPayload])
  const contextValue = useMemo<NotificationContextValue>(() => {
    const { showSuccessNotif, showErrorNotif, showInfoNotif, showNotif, hideNotif } = events
    return {
      notifPayload,
      showSuccessNotif,
      showErrorNotif,
      showInfoNotif,
      showNotif,
      hideNotif
    }
  }, [notifPayload, events])

  return (
    <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
  )
}

export default NotificationProvider
